import {
    type FlexAlignType,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ModalSurfaceStyleConstantType,
    ModalGutterStyleConstantType,
    ModalCloseStyleConstantType,
    ModalPortalNameConstantType,
    ModalMotionConstantType,
    ModalAlignContentProp,
    ModalDynamicStyleType,
    ModalGutterSpacesType,
    ModalContentJustify,
    ModalCloseStyleType,
    ModalMotionType
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    SNACK_BAR_PORTAL_NAME,
    POPOVER_PORTAL_NAME
} from "../../helpers/portalize/context";
import {
    getMotionDuration,
    MOTION_EASINGS,
    MOTION_INTENTS
} from "../../helpers/motion";
import {
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHitArea
} from "../../helpers/theme/control";

export const MODAL_PORTAL_NAME = "modal-system";

export const MODAL_FORWARDED_KEYS: Array<string> = [
    "Escape",
    "Tab"
];

export const MODAL_EDITABLE_TAG_NAMES: Array<string> = [
    "TEXTAREA",
    "SELECT",
    "INPUT"
];

export const MODAL_TAB_OWNER_DATA_KEY = "tabOwner";

export const MODAL_PORTAL_LAYERS: Record<string, number> = {
    [SNACK_BAR_PORTAL_NAME]: 4,
    [POPOVER_PORTAL_NAME]: 5,
    "bottomSheet-system": 1,
    "modal-system": 2,
    "toast-system": 3,
    "menu-system": 0
};

export const MODAL_CONTENT_ALIGNMENTS: Record<ModalAlignContentProp, FlexAlignType> = {
    free: "flex-start",
    center: "center"
};

export const MODAL_CONTENT_JUSTIFICATIONS: Record<ModalAlignContentProp, ModalContentJustify> = {
    free: "flex-start",
    center: "center"
};

export const MODAL_GUTTER_SPACES: Record<ModalAlignContentProp, ModalGutterSpacesType> = {
    center: {
        horizontal: "spacingLg",
        bottom: "spacingLg",
        top: "spacingLg"
    },
    free: {
        horizontal: "spacingLg",
        bottom: "spacingLg",
        top: "spacingXl"
    }
};

export const getModalPortalName = ({
    parentPortalName,
    stackPortalName,
    portalName
}: ModalPortalNameConstantType): string => {
    const requestedPortalName = portalName ? portalName : MODAL_PORTAL_NAME;

    const hostPortalName = parentPortalName ? parentPortalName : stackPortalName;

    if(!hostPortalName) {
        return requestedPortalName;
    }

    const requestedLayer = MODAL_PORTAL_LAYERS[requestedPortalName];

    const hostLayer = MODAL_PORTAL_LAYERS[hostPortalName];

    if(requestedLayer === undefined || hostLayer === undefined || requestedLayer >= hostLayer) {
        return requestedPortalName;
    }

    return hostPortalName;
};

export const getModalMotion = ({
    isReduceMotionEnabled,
    closeAnimationDelay,
    openAnimationDelay,
    durations
}: ModalMotionConstantType): ModalMotionType => {
    const closeDuration = closeAnimationDelay ?? MOTION_INTENTS.panelExit.duration;

    const openDuration = openAnimationDelay ?? MOTION_INTENTS.panelEnter.duration;

    return {
        closeEasing: MOTION_EASINGS[MOTION_INTENTS.panelExit.easing],
        openEasing: MOTION_EASINGS[MOTION_INTENTS.panelEnter.easing],
        closeFadeDuration: getMotionDuration({
            duration: closeDuration,
            isReduceMotionEnabled,
            motion: "fade",
            durations
        }),
        closeMoveDuration: getMotionDuration({
            duration: closeDuration,
            isReduceMotionEnabled,
            durations
        }),
        openFadeDuration: getMotionDuration({
            duration: openDuration,
            isReduceMotionEnabled,
            motion: "fade",
            durations
        }),
        openMoveDuration: getMotionDuration({
            duration: openDuration,
            isReduceMotionEnabled,
            durations
        })
    };
};

export const getModalCloseStyle = ({
    controlHeights,
    touchTargets,
    pointerType,
    typography,
    iconSizes,
    radiuses,
    borders,
    spaces
}: ModalCloseStyleConstantType): ModalCloseStyleType => {
    const hitArea = getHitArea({
        visualSize: controlHeights.small,
        touchTargets,
        pointerType
    });

    const iconInset = (controlHeights.small - iconSizes.small) / 2;

    return {
        top: spaces.spacingLg + (typography.titleLargeSize.lineHeight - hitArea.minHeight) / 2,
        end: spaces.spacingLg - (hitArea.minWidth - iconSizes.small) / 2,
        borderRadius: concentric({
            inset: spaces.spacingLg - iconInset + borders.line,
            outer: radiuses.surface,
            role: radiuses.actions
        })
    };
};

export const getModalSurfaceStyle = ({
    shapeConfigs,
    shadowAlpha,
    elevations,
    radiuses,
    borders,
    colors,
    spaces
}: ModalSurfaceStyleConstantType): ViewStyle => {
    return {
        ...getSurfaceStyle({
            surface: "dialog",
            shadowAlpha,
            elevations,
            borders,
            colors
        }),
        maxWidth: shapeConfigs.dialogMaxWidth,
        ...getCornerStyle({
            radius: radiuses.surface,
            shapeConfigs
        }),
        padding: spaces.spacingLg,
        gap: spaces.spacingMd
    };
};

export const getModalGutterStyle = ({
    alignContent,
    spaces
}: ModalGutterStyleConstantType): ViewStyle => {
    const gutterSpaces = MODAL_GUTTER_SPACES[alignContent];

    return {
        paddingHorizontal: spaces[gutterSpaces.horizontal],
        paddingBottom: spaces[gutterSpaces.bottom],
        paddingTop: spaces[gutterSpaces.top]
    };
};

const stylesheet = StyleSheet.create({
    dialog: {
        pointerEvents: "box-none",
        flexDirection: "column",
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0,
        top: 0
    },
    gutter: {
        pointerEvents: "box-none",
        alignItems: "center",
        alignSelf: "stretch",
        flexShrink: 1
    },
    passThroughContainer: {
        pointerEvents: "box-none"
    },
    container: {
        flexDirection: "column",
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0,
        top: 0
    },
    scrim: {
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0,
        top: 0
    },
    surface: {
        borderStyle: "solid",
        flexShrink: 1,
        width: "100%"
    },
    hiddenScrim: {
        pointerEvents: "none"
    },
    close: {
        position: "absolute"
    }
});

export const useStyles = ({
    alignContent,
    shapeConfigs,
    shadowAlpha,
    closeStyle,
    elevations,
    radiuses,
    borders,
    colors,
    spaces
}: ModalDynamicStyleType) => {
    const styles = {
        dialog: {
            justifyContent: MODAL_CONTENT_JUSTIFICATIONS[alignContent],
            alignItems: MODAL_CONTENT_ALIGNMENTS[alignContent]
        } as Mutable<ViewStyle>,
        scrim: {
            backgroundColor: colors.system.scrim
        } as Mutable<ViewStyle>,
        surface: getModalSurfaceStyle({
            shapeConfigs,
            shadowAlpha,
            elevations,
            radiuses,
            borders,
            colors,
            spaces
        }) as Mutable<ViewStyle>,
        gutter: getModalGutterStyle({
            alignContent,
            spaces
        }) as Mutable<ViewStyle>,
        close: {
            top: closeStyle.top,
            end: closeStyle.end
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
